import React, { useState } from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import type { Product, ProductVariant } from "../types";
import { useCart } from "../contexts/cartContext";

interface Props {
  product: Product;
}

const ProductCard = ({ product }: Props) => {
  const { addToCart } = useCart();
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  const handleAddToCart = () => {
    const variant: ProductVariant | undefined = product.variants.find(
      (v) => v.size === selectedSize
    );
    if (!variant) return;
    addToCart(variant);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        "&:hover .quick-add": { transform: "translateY(0)" },
        "&:hover .product-img": { transform: "scale(1.05)" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: "#f5f5f5",
          aspectRatio: "3/4",
        }}
      >
        <Box
          component="img"
          className="product-img"
          src={product.image}
          alt={product.name}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center",
            transition: "transform 0.5s ease",
            display: "block",
          }}
        />

        {product.isNew && (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
            }}
          >
            <Chip
              label="New"
              size="small"
              sx={{
                bgcolor: "black",
                color: "white",
                borderRadius: 0,
                fontSize: "0.6rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                height: "auto",
                py: 0.25,
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>
        )}

        <Box
          className="quick-add"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: "rgba(0,0,0,0.92)",
            transform: "translateY(100%)",
            transition: "transform 0.3s ease",
            p: 1.5,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            {product.sizes.map((size) => (
              <Button
                key={size}
                onClick={() => setSelectedSize(size)}
                size="small"
                variant={selectedSize === size ? "contained" : "outlined"}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.5,
                  fontSize: "0.68rem",
                  fontWeight: 500,
                  ...(selectedSize === size
                    ? {
                        bgcolor: "white",
                        color: "black",
                        borderColor: "white",
                        "&:hover": { bgcolor: "#e5e5e5" },
                      }
                    : {
                        borderColor: "#525252",
                        color: "#d4d4d4",
                        "&:hover": { borderColor: "white", color: "white" },
                      }),
                }}
              >
                {size}
              </Button>
            ))}
          </Box>

          <Button
            onClick={handleAddToCart}
            disabled={!selectedSize}
            fullWidth
            variant="contained"
            size="small"
            sx={{
              fontSize: "0.68rem",
              letterSpacing: "0.1em",
              fontWeight: 600,
              py: 1,
              ...(added
                ? { bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }
                : {
                    bgcolor: "white",
                    color: "black",
                    "&:hover": { bgcolor: "#e5e5e5" },
                    "&.Mui-disabled": { bgcolor: "#404040", color: "#737373" },
                  }),
            }}
          >
            {added ? "Added!" : selectedSize ? "Add to Cart" : "Select a Size"}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 0.25 }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: "#111111", lineHeight: 1.35 }}
        >
          {product.name}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#111111" }}
          >
            {product.salePrice != null ? `$${product.salePrice}` : "—"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductCard;
